import React, { forwardRef, useMemo } from 'react';
import { useDebounce, useMeasure } from 'react-use';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { mergeRefs } from 'src/core/utils';
import { RecenterIcon } from 'src/svgs';
import { MinusIcon, PlusIcon } from 'src/svgs/Viagogo';

import { ColorBandProps } from './shared/components/ColorBand';
import { CondensedColorBand } from './shared/components/ColorBand/CondensedColorBand';
import { StyledDiv } from './VenueMap.styled';
import * as styles from './VenueMapContentCondensed.css';

type VenueMapContentCondensedProps = {
  localRef:
    | React.RefObject<HTMLDivElement>
    | React.Dispatch<React.SetStateAction<HTMLDivElement | null>>;
  svg: string | TrustedHTML;
  blurMap: boolean;
  hideMap?: boolean;
  colorBandProps: ColorBandProps | undefined;
  compQuantityFilters?: string[];
  cornerContent?: React.ReactNode;
  statsContent: React.ReactNode;
  mapActionContainerClassname?: string;
  v3?: boolean;
  hideControls?: boolean;
  canRecenter?: boolean;
  condensedControl?: boolean;
  onZoom: (zoomIn?: boolean) => void;
  onReset: () => void;
};

export const VenueMapContentCondensed = forwardRef<
  HTMLDivElement,
  VenueMapContentCondensedProps
>(function VenueMapContentCondensed(
  {
    localRef,
    svg,
    blurMap,
    hideMap,
    colorBandProps,
    cornerContent,
    statsContent,
    mapActionContainerClassname,
    v3,
    hideControls = false,
    canRecenter,
    condensedControl,
    onZoom,
    onReset,
  },
  ref
) {
  const [containerRef, { width }] = useMeasure<HTMLDivElement>();
  useDebounce(() => onReset(), 100, [width]);

  const [shouldShowControls, shouldShowPlusMinors, shouldShowRecenter] =
    useMemo(() => {
      const shouldShowControls = !hideControls || condensedControl;
      const shouldShowPlusMinors = !condensedControl;
      const shouldShowRecenter = canRecenter;
      return [shouldShowControls, shouldShowPlusMinors, shouldShowRecenter];
    }, [canRecenter, condensedControl, hideControls]);

  return (
    <Stack
      ref={containerRef}
      direction="column"
      width="full"
      height="full"
      gap="s"
      className={styles.condensedContainer}
    >
      <StyledDiv
        id={'event-detail-map'}
        ref={mergeRefs(localRef, ref)}
        dangerouslySetInnerHTML={v3 ? undefined : { __html: svg }}
        setTransitionAnimation={true}
        blurMap={blurMap}
        hideMap={hideMap}
        style={{ minHeight: '1px', minWidth: '1px' }} // Set min width/height to avoid svgPanZoom to shrink to 0, which is not recoverable
      />

      <Stack
        width="full"
        justifyContent="spaceBetween"
        className={mapActionContainerClassname}
      >
        <Stack width="full" alignItems="center" gap="m">
          {cornerContent && (
            <div className={styles.statsContentWrapper}>{cornerContent}</div>
          )}
          {shouldShowControls && (
            <Stack width="full" alignItems="center" gap="m">
              {shouldShowPlusMinors && (
                <div className={styles.mapControlsWrapper}>
                  <PlusIcon
                    withHoverEffect
                    size={vars.iconSize.xl}
                    className={styles.plusIconWrapper}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      onZoom(true);
                    }}
                  />
                  <MinusIcon
                    withHoverEffect
                    size={vars.iconSize.xl}
                    className={styles.minusIconWrapper}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      onZoom(false);
                    }}
                  />
                </div>
              )}
              {shouldShowRecenter && (
                <div className={styles.mapControlsWrapper}>
                  <RecenterIcon
                    withHoverEffect
                    size={vars.iconSize.xl}
                    className={styles.recenterIconWrapper}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      onReset();
                    }}
                  />
                </div>
              )}
            </Stack>
          )}
          <div className={styles.statsContentWrapper}>{statsContent}</div>
        </Stack>
        <Stack alignItems="center">
          {colorBandProps && (
            <div className={styles.colorBandContainer}>
              <CondensedColorBand {...colorBandProps} />
            </div>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
});
